/**
 * @file Access helpers for top-level app and board routes.
 */

import {
    getBoardRegistryEntry,
    isBoardNavigationEnabled,
    isRegisteredBoardViewKey,
} from "../../shared/board/boardRegistry.js";

const ADMIN_ONLY_VIEWS = Object.freeze(["settings", "sandbox"]);
const DRUG_PERMISSION_KEYS = Object.freeze([
    "drugAdmin",
    "viewDashboard",
    "viewHistory",
    "viewAudits",
    "logUse",
    "restock",
    "relocate",
    "correction",
    "logLoss",
    "startAudit",
    "finalizeAudit",
    "deleteAudit",
    "manageInventory",
    "manageLocations",
    "manageRoles",
]);

function isAdminProfile(profile) {
    return profile?.role === "admin";
}

function getProfilePermissions(profile) {
    return profile?.permissions || [];
}

/**
 * Returns whether a user profile can access any Controlled Drugs route.
 * @param {object|null} profile - Current user profile.
 * @returns {boolean} True when the user has drug access.
 */
export function hasDrugAccess(profile) {
    if (isAdminProfile(profile)) {return true;}

    const permissions = getProfilePermissions(profile);
    if (permissions.includes("drugs")) {return true;}

    const drugPermissions = profile?.drugPermissions || {};
    return DRUG_PERMISSION_KEYS.some((key) => drugPermissions[key] === true);
}

/**
 * Returns whether a user profile has the staged Ordering capability.
 * Route availability remains separate until the guarded view is implemented.
 * @param {object|null} profile - Current user profile.
 * @returns {boolean} True when the user has Ordering capability.
 */
export function hasOrderingAccess(profile) {
    return isAdminProfile(profile) ||
        getProfilePermissions(profile).includes("ordering");
}

/**
 * Returns whether a user profile has Compliance module access.
 * Category-content access remains a separate team-membership check.
 * @param {object|null} profile - Current user profile.
 * @returns {boolean} True when the user has Compliance module access.
 */
export function hasComplianceAccess(profile) {
    return isAdminProfile(profile) ||
        getProfilePermissions(profile).includes("compliance");
}

/**
 * Returns whether a user profile can access a route view.
 * @param {object|null} profile - Current user profile.
 * @param {string} viewName - Route view key.
 * @returns {boolean} True when the view is available to this profile.
 */
export function canAccessView(profile, viewName) {
    if (viewName === "tasks") {return true;}
    if (ADMIN_ONLY_VIEWS.includes(viewName)) {return isAdminProfile(profile);}
    if (viewName === "drugs") {return hasDrugAccess(profile);}
    if (viewName === "recalls") {
        return isAdminProfile(profile) ||
            getProfilePermissions(profile).includes("recalls");
    }
    if (viewName === "ordering") {
        return hasOrderingAccess(profile);
    }
    if (viewName === "compliance") {
        return hasComplianceAccess(profile);
    }
    if (viewName === "calendar") {
        return isAdminProfile(profile) ||
            getProfilePermissions(profile).includes("calendar");
    }

    if (isRegisteredBoardViewKey(viewName)) {
        if (!isBoardNavigationEnabled(viewName)) {
            return false;
        }
        const permissionKey = getBoardRegistryEntry(viewName)?.permissionKey;
        return !permissionKey ||
            isAdminProfile(profile) ||
            getProfilePermissions(profile).includes(permissionKey);
    }

    return false;
}

/**
 * Returns a warning message for denied known app routes.
 * @param {object|null} profile - Current user profile.
 * @param {string} viewName - Route view key.
 * @returns {string|null} Denial warning, or null if allowed/not protected.
 */
export function getViewAccessDenialMessage(profile, viewName) {
    if (canAccessView(profile, viewName)) {return null;}

    if (viewName === "settings") {
        return "Access denied: Settings view requires admin role.";
    }
    if (viewName === "sandbox") {
        return "Access denied: Sandbox view requires admin role.";
    }
    if (viewName === "drugs") {
        return "Access denied: Drugs view requires at least one drug permission.";
    }
    if (viewName === "recalls") {
        return "Access denied: Recalls view requires permission.";
    }
    if (viewName === "ordering") {
        return "Access denied: Ordering view requires permission.";
    }
    if (viewName === "compliance") {
        return "Access denied: Compliance view requires permission.";
    }
    if (viewName === "calendar") {
        return "Access denied: Calendar view requires permission.";
    }
    if (isRegisteredBoardViewKey(viewName)) {
        const board = getBoardRegistryEntry(viewName);
        const label = board?.display?.pluralLabel || viewName;
        if (!isBoardNavigationEnabled(viewName)) {
            return `${label} view is not available yet.`;
        }
        return `Access denied: ${label} view requires permission.`;
    }

    return null;
}
